import React, { useState, useEffect } from 'react';
import { CardComponentProps } from '@/lib/cards';
import { useChartSafeRender } from '@/hooks/useChartSafeRender';
import { getDashboardSla, SlaWindowStats } from '@/api/dashboard';

/**
 * SLA 7-Day Card
 * Displays 7-day SLA metrics using a chart
 */
export const SLA7dCard: React.FC<CardComponentProps> = ({
  card,
  userContext,
  onError,
}) => {
  const [data, setData] = useState<SlaWindowStats | null>(null);
  const [loading, setLoading] = useState(true);
  const chart = useChartSafeRender();

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const response = await getDashboardSla();
        setData(response['7d']);
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [onError]);

  if (loading) {
    return <div style={{ padding: '1rem', color: '#666' }}>Loading SLA data...</div>;
  }

  if (!data) {
    return <div style={{ padding: '1rem', color: '#666' }}>No SLA data available</div>;
  }

  const bars = [
    { label: 'Open', value: data.open, color: '#ffc107' },
    { label: 'Closed On Time', value: data.closedOnTime, color: '#28a745' },
    { label: 'Breached', value: data.breached, color: '#dc3545' },
  ];
  const maxValue = Math.max(...bars.map((b) => b.value), 1);

  return (
    <div ref={chart.containerRef} style={{ padding: '1rem', minHeight: '200px' }}>
      <div style={{ fontSize: '0.8rem', color: '#999', marginBottom: '0.75rem' }}>
        {new Date(data.periodStart).toLocaleDateString()} –{' '}
        {new Date(data.periodEnd).toLocaleDateString()}
      </div>
      {chart.isDimensionReady ? (
        <div key={chart.resizeKey} style={{ width: '100%', height: '160px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-around',
              height: '100%',
              gap: '0.5rem',
            }}
          >
            {bars.map((b) => (
              <div key={b.label} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                  {b.value}
                </div>
                <div
                  style={{
                    backgroundColor: b.color,
                    height: `${Math.round((b.value / maxValue) * 100)}px`,
                    borderRadius: '4px',
                    marginBottom: '0.5rem',
                  }}
                />
                <div style={{ fontSize: '0.75rem', color: '#666' }}>{b.label}</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', color: '#999' }}>Computing chart dimensions...</div>
      )}
    </div>
  );
};

SLA7dCard.displayName = 'SLA7dCard';

export default SLA7dCard;
